import React, { useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';

const { FiCpu, FiTrash2, FiActivity, FiPlay, FiExternalLink, FiSearch, FiX } = FiIcons;

const SmallToggle = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={cn(
      "relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none",
      enabled ? "bg-green-500" : "bg-slate-700"
    )}
  >
    <span
      className={cn(
        "absolute h-2 w-2 transform rounded-full bg-white transition-transform",
        enabled ? "translate-x-4" : "translate-x-1"
      )}
    />
  </button>
);

const STRATS = [
  ['confluence', 'Confluence'], ['momentum', 'Momentum'], ['breakout', 'Breakout'],
  ['meanReversion', 'Mean-Reversion'], ['strict', 'Strict'], ['scalp', 'Scalp']
];
const TFS = ['1m', '5m', '15m', '1h', '4h'];

export default function WatchlistPanel({ watchlist, onRemove, onUpdateWatch, isBotActive, setIsBotActive, settings }) {
  const gStrat = settings?.strategy || 'confluence';
  const gTf = settings?.botTimeframe || '15m';
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return watchlist;
    return watchlist.filter((t) =>
      (t.symbol || '').toLowerCase().includes(q) ||
      (t.name || '').toLowerCase().includes(q) ||
      (t.address || '').toLowerCase().includes(q)
    );
  }, [watchlist, search]);

  return (
    <div className="glass-panel rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-4">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiCpu} className="text-blue-400 text-xl" />
          <h2 className="text-lg font-bold text-white">Auto-Bot</h2>
        </div>
        <div className="flex items-center gap-2 bg-[#0B0E14] px-3 py-1.5 rounded-lg border border-slate-800">
          <span className={cn("text-[10px] font-bold uppercase tracking-widest", isBotActive ? "text-green-400" : "text-slate-500")}>
            {isBotActive ? 'Active' : 'Paused'}
          </span>
          <SmallToggle enabled={isBotActive} onChange={() => setIsBotActive(!isBotActive)} />
        </div>
      </div>

      {watchlist.length > 0 && (
        <div className="relative mb-3">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by symbol, name, or address…"
            className="input-field text-xs pl-9 pr-8"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              title="Clear search"
            >
              <SafeIcon icon={FiX} className="text-sm" />
            </button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {watchlist.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <SafeIcon icon={FiActivity} className="text-3xl mx-auto mb-2 opacity-20" />
            <p className="text-xs font-medium">Watchlist is empty.</p>
            <p className="text-[10px]">Add tokens from the Trading Panel.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <SafeIcon icon={FiSearch} className="text-3xl mx-auto mb-2 opacity-20" />
            <p className="text-xs font-medium">No tokens match "{search}".</p>
          </div>
        ) : (
          filtered.map(token => {
            const isOverride = !!(token.strategy || token.timeframe);
            const effStrat = token.strategy || gStrat;
            const effTf = token.timeframe || gTf;
            return (
              <div key={token.address} className="bg-[#0B0E14] rounded-xl p-3 border border-slate-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-white">{token.symbol}</span>
                      {token.dexUrl && (
                        <a href={token.dexUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                          className="text-blue-400 hover:text-blue-300" title="View on DexScreener">
                          <SafeIcon icon={FiExternalLink} className="text-xs" />
                        </a>
                      )}
                      {isBotActive && (
                        <span className="flex h-2 w-2 relative">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                      {token.address.substring(0, 6)}...{token.address.slice(-4)}
                    </div>
                  </div>
                  <button onClick={() => onRemove(token.address)}
                    className="text-slate-500 hover:text-red-400 p-1.5 rounded-md hover:bg-red-500/10 transition-colors flex-shrink-0"
                    title="Remove from Bot Watchlist">
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>

                {/* Per-token strategy / timeframe (Default = use the global Auto-Bot setting) */}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <select
                    value={token.strategy || ''}
                    onChange={(e) => onUpdateWatch(token.address, e.target.value, token.timeframe || '')}
                    className="input-field text-[10px] py-1 appearance-none cursor-pointer"
                    title="Signal strategy for this token"
                  >
                    <option value="">Strategy: Default</option>
                    {STRATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select
                    value={token.timeframe || ''}
                    onChange={(e) => onUpdateWatch(token.address, token.strategy || '', e.target.value)}
                    className="input-field text-[10px] py-1 appearance-none cursor-pointer"
                    title="Candle timeframe for this token"
                  >
                    <option value="">Timeframe: Default</option>
                    {TFS.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div className={cn("text-[9px] mt-1", isOverride ? "text-blue-300" : "text-slate-500")}>
                  Running <strong>{effStrat}</strong> / <strong>{effTf}</strong> {isOverride ? '(override)' : '(global default)'}
                </div>
              </div>
            );
          })
        )}
      </div>

      {isBotActive && watchlist.length > 0 && (
        <div className="mt-4 text-[10px] text-slate-400 bg-blue-500/10 border border-blue-500/20 p-2 rounded-lg flex items-start gap-2">
          <SafeIcon icon={FiPlay} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <p>Global default is <strong className="text-white">{gStrat}</strong> @ <strong className="text-white">{gTf}</strong> (Settings). Override per token above; "Default" follows the global. Bot re-evaluates every 30s.</p>
        </div>
      )}
    </div>
  );
}